import React, { useEffect, useMemo, useState } from "react";
import { Star, MessageSquareHeart, Send, Check, Instagram } from "lucide-react";
import { useLang } from "../lib/i18n";
import { api, API } from "../lib/api";

function StarRow({ value, size = 16, interactive = false, onChange, hover, onHover }) {
  return (
    <div
      className="inline-flex items-center gap-0.5"
      onMouseLeave={interactive ? () => onHover?.(0) : undefined}
      data-testid="star-row"
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || value) >= n;
        const Cmp = interactive ? "button" : "span";
        return (
          <Cmp
            key={n}
            type={interactive ? "button" : undefined}
            onClick={interactive ? () => onChange?.(n) : undefined}
            onMouseEnter={interactive ? () => onHover?.(n) : undefined}
            data-testid={interactive ? `star-${n}` : undefined}
            aria-label={interactive ? `${n} star${n > 1 ? "s" : ""}` : undefined}
            className={interactive ? "cursor-pointer p-0.5 transition-transform hover:scale-125" : "inline-block"}
          >
            <Star
              className="transition-colors"
              style={{ width: size, height: size }}
              fill={active ? "#F4A261" : "none"}
              stroke={active ? "#F4A261" : "#B9BAC0"}
              strokeWidth={2}
            />
          </Cmp>
        );
      })}
    </div>
  );
}

function ReviewCard({ r, onShare, sharing }) {
  const initials = (r.name || "").trim().slice(0, 1).toUpperCase() || "★";
  const date = new Date(r.created_at);
  const dateStr = date.toLocaleDateString(r.lang === "fr" ? "fr-FR" : "en-US",
    { day: "numeric", month: "short" });
  const isFr = r.lang === "fr";
  const canShare = r.rating === 5;
  return (
    <article
      data-testid="review-card"
      className="bg-white rounded-2xl border border-[#E8E6E1] p-5 flex flex-col gap-3 hover:shadow-md transition-shadow"
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F4A261] to-[#E07A5F] text-white grid place-items-center font-heading text-sm shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#3D405B] truncate">
            {r.name?.trim() || (isFr ? "Un parent anonyme" : "An anonymous parent")}
          </div>
          <div className="text-[11px] text-[#8A8D91]">{dateStr}</div>
        </div>
        <StarRow value={r.rating} size={14} />
      </div>
      <p className="text-sm text-[#3D405B]/85 leading-relaxed whitespace-pre-wrap break-words">
        {r.text}
      </p>
      {canShare && (
        <button
          onClick={() => onShare?.(r)}
          disabled={sharing}
          data-testid={`review-share-${r.id}`}
          className="mt-auto self-start inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#E07A5F] to-[#F4A261] text-white px-3 py-1.5 text-xs font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <Instagram className="w-3 h-3" />
          {sharing ? (isFr ? "Génération…" : "Generating…") : (isFr ? "Partager en story" : "Share as story")}
        </button>
      )}
    </article>
  );
}

export default function ReviewsSection() {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const [data, setData] = useState({ items: [], average: 0, count: 0 });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState("all"); // all | 5 | 4 | 3
  const [sharingId, setSharingId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/reviews?limit=24`);
      setData(r.data || { items: [], average: 0, count: 0 });
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const items = data.items || [];
    if (filter === "all") return items;
    const min = Number(filter);
    return items.filter((it) => it.rating >= min);
  }, [data.items, filter]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating || text.trim().length < 3 || status === "sending") return;
    setStatus("sending");
    try {
      await api.post("/reviews", { rating, text: text.trim(), name: name.trim(), lang });
      setStatus("sent");
      setRating(0); setText(""); setName("");
      load();
      setTimeout(() => setStatus("idle"), 3500);
    } catch {
      setStatus("error");
    }
  };

  const share = async (r) => {
    setSharingId(r.id);
    try {
      const res = await fetch(`${API}/reviews/${r.id}/story`);
      if (!res.ok) throw new Error("story");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `avis-le-gang-${(r.name || "story").toLowerCase().replace(/[^a-z0-9]/g, "-").slice(0, 20)}.jpg`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setSharingId(null);
    }
  };

  const t = {
    badge: isFr ? "Vos retours" : "Your feedback",
    title: isFr ? "Ce que les parents disent du Gang" : "What parents say about The Gang",
    subtitle: isFr
      ? "Un mot, une étoile, une pensée — chaque avis compte 💛"
      : "One word, one star, one thought — every review counts 💛",
    ratingLabel: isFr ? "Note globale" : "Overall rating",
    countSuffix: (n) => isFr
      ? `basée sur ${n} avis`
      : `based on ${n} review${n === 1 ? "" : "s"}`,
    formTitle: isFr ? "Laisser un avis" : "Leave a review",
    formToggle: isFr ? "Écrire mon avis" : "Write my review",
    starsLabel: isFr ? "Votre note" : "Your rating",
    textLabel: isFr ? "Votre avis" : "Your review",
    textPlaceholder: isFr
      ? "Ce que vous avez aimé, ce qui vous a aidé·e, ce qui pourrait être mieux…"
      : "What you loved, what helped you, what could be better…",
    nameLabel: isFr ? "Prénom (facultatif)" : "First name (optional)",
    namePlaceholder: isFr ? "Marie, Papa d'Adam…" : "Marie, Adam's dad…",
    submit: isFr ? "Publier mon avis" : "Publish my review",
    sending: isFr ? "Envoi…" : "Sending…",
    sent: isFr ? "Merci ! Votre avis est publié 💛" : "Thank you! Your review is live 💛",
    error: isFr ? "Oups, réessayez dans un instant." : "Oops, please try again.",
    empty: isFr
      ? "Aucun avis pour le moment. Soyez le premier à en laisser un !"
      : "No reviews yet. Be the first to leave one!",
    emptyFiltered: isFr ? "Aucun avis dans cette catégorie." : "No reviews in this category yet.",
    filterAll: isFr ? "Tous" : "All",
  };

  const FILTERS = [
    { key: "all", label: t.filterAll },
    { key: "5",   label: "5★" },
    { key: "4",   label: "4★+" },
    { key: "3",   label: "3★+" },
  ];

  return (
    <section
      data-testid="reviews-section"
      className="rounded-3xl border border-[#E8E6E1] bg-gradient-to-br from-[#FDFBF7] to-white p-6 md:p-10"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#F4A261] font-semibold inline-flex items-center gap-2">
            <MessageSquareHeart className="w-4 h-4" /> {t.badge}
          </div>
          <h2 className="font-heading text-3xl md:text-4xl text-[#3D405B] mt-2">{t.title}</h2>
          <p className="text-[#3D405B]/70 text-sm md:text-base mt-1">{t.subtitle}</p>
        </div>
        {data.count > 0 && (
          <div className="text-right shrink-0" data-testid="reviews-summary">
            <div className="flex items-center justify-end gap-2">
              <span className="font-heading text-3xl text-[#3D405B]">{data.average.toFixed(1)}</span>
              <StarRow value={Math.round(data.average)} size={18} />
            </div>
            <div className="text-xs text-[#8A8D91] mt-0.5">{t.countSuffix(data.count)}</div>
          </div>
        )}
      </div>

      {/* Write a review */}
      <div className="mt-6">
        {!expanded ? (
          <button
            onClick={() => setExpanded(true)}
            data-testid="review-open-form"
            className="inline-flex items-center gap-2 bg-[#F4A261] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#e08f4e] transition-colors shadow-sm"
          >
            <Star className="w-4 h-4" fill="#fff" /> {t.formToggle}
          </button>
        ) : (
          <form
            onSubmit={submit}
            data-testid="review-form"
            className="bg-white rounded-2xl border border-[#F4A261]/40 p-5 md:p-6 space-y-4"
          >
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-[#8A8D91]">
                {t.starsLabel}
              </label>
              <div className="mt-1">
                <StarRow value={rating} hover={hover} onHover={setHover} onChange={setRating} interactive size={28} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-[#8A8D91]">
                {t.textLabel}
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 600))}
                placeholder={t.textPlaceholder}
                data-testid="review-text"
                rows={4}
                className="mt-1 w-full bg-[#FDFBF7] border border-[#E8E6E1] rounded-xl px-4 py-3 text-sm text-[#3D405B] focus:outline-none focus:ring-2 focus:ring-[#F4A261]/40 focus:border-[#F4A261] resize-none"
              />
              <div className="text-[11px] text-[#8A8D91] mt-1 text-right">{text.length}/600</div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-[#8A8D91]">
                {t.nameLabel}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 60))}
                placeholder={t.namePlaceholder}
                data-testid="review-name"
                className="mt-1 w-full bg-[#FDFBF7] border border-[#E8E6E1] rounded-full px-4 py-2 text-sm text-[#3D405B] focus:outline-none focus:ring-2 focus:ring-[#F4A261]/40 focus:border-[#F4A261]"
              />
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              {status === "sent" ? (
                <div className="text-sm text-[#73A38B] font-semibold inline-flex items-center gap-1.5" data-testid="review-sent">
                  <Check className="w-4 h-4" /> {t.sent}
                </div>
              ) : status === "error" ? (
                <div className="text-sm text-[#E07A5F] font-medium">{t.error}</div>
              ) : (
                <span />
              )}
              <button
                type="submit"
                data-testid="review-submit"
                disabled={!rating || text.trim().length < 3 || status === "sending"}
                className="inline-flex items-center gap-2 bg-[#3D405B] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#2f3147] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="w-3.5 h-3.5" />
                {status === "sending" ? t.sending : t.submit}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Recent reviews */}
      <div className="mt-8">
        {data.items.length === 0 ? (
          <div className="text-center text-[#8A8D91] text-sm py-8" data-testid="reviews-empty">
            {t.empty}
          </div>
        ) : (
          <>
            {/* Star filters */}
            <div className="flex flex-wrap gap-2 mb-5" data-testid="reviews-filters">
              {FILTERS.map((f) => {
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    data-testid={`reviews-filter-${f.key}`}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
                      active
                        ? "bg-[#F4A261] text-white border-[#F4A261]"
                        : "bg-white text-[#3D405B] border-[#E8E6E1] hover:border-[#F4A261]"
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
            {filtered.length === 0 ? (
              <div className="text-center text-[#8A8D91] text-sm py-6" data-testid="reviews-empty-filter">
                {t.emptyFiltered}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.slice(0, 6).map((r) => (
                  <ReviewCard
                    key={r.id}
                    r={r}
                    onShare={share}
                    sharing={sharingId === r.id}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
